// PAWPRO — Motion design (premium polish, lean perf)
//
// Stratégie : reveals & section reveals gérés par CSS + IntersectionObserver
// (pawpro-reveal.js). Ici on garde uniquement les animations spéciales :
//   - Hero V2 (entrée timeline + fade-out scroll-driven)
//   - Count-ups KPI (one-shot)
//   - Magnetic hover sur tous les CTAs primary lg
//   - Parallax léger sur images héro spécifiques
//
// Respects prefers-reduced-motion. Re-runs on shopify:section:load.

(function () {
  'use strict';

  function waitForLibs(cb, attempts) {
    attempts = attempts || 0;
    if (window.gsap && window.ScrollTrigger) {
      gsap.registerPlugin(ScrollTrigger);
      cb();
      return;
    }
    if (attempts > 100) {
      // GSAP/ScrollTrigger not loaded after timeout — silent fail (animations disabled, content still functional)
      return;
    }
    setTimeout(function () { waitForLibs(cb, attempts + 1); }, 50);
  }

  const prefersReduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  const PAWPRO = window.PAWPRO || {};
  PAWPRO.motion = {
    initialized: false,

    init: function () {
      if (this.initialized) return;
      this.initialized = true;

      if (prefersReduced) {
        this._initSimpleReveals();
        return;
      }

      this._initHeroV2();
      this._initCountUps();
      this._initMagneticHovers();
      this._initSubtleParallax();

      requestAnimationFrame(() => ScrollTrigger.refresh());
      if (document.readyState !== 'complete') {
        window.addEventListener('load', () => setTimeout(() => ScrollTrigger.refresh(), 100));
      }
    },

    // ── HERO V2 — load timeline + content fade-out on scroll ─────────────
    _initHeroV2: function () {
      const hero = document.querySelector('[data-pawpro-hero-v2]');
      if (!hero) return;

      const content = hero.querySelector('[data-pawpro-hero-content]');
      const eyebrow = hero.querySelector('[data-pawpro-hero-eyebrow]');
      const headlineLines = hero.querySelectorAll('[data-pawpro-text-reveal]');
      const sub = hero.querySelector('[data-pawpro-hero-sub]');
      const ctas = hero.querySelector('[data-pawpro-hero-ctas]');
      const kpis = hero.querySelector('[data-pawpro-hero-kpis]');
      const scrollIndicator = hero.querySelector('[data-pawpro-hero-scroll-cue]');

      gsap.set([eyebrow, sub, ctas, kpis], { opacity: 0, y: 40 });
      gsap.set(scrollIndicator, { opacity: 0 });

      headlineLines.forEach(line => {
        const text = line.textContent;
        line.textContent = '';
        const wrapper = document.createElement('span');
        wrapper.style.display = 'inline-block';
        wrapper.style.overflow = 'hidden';
        wrapper.style.verticalAlign = 'top';
        const inner = document.createElement('span');
        inner.style.display = 'inline-block';
        inner.style.transform = 'translateY(110%)';
        inner.style.willChange = 'transform';
        inner.textContent = text;
        wrapper.appendChild(inner);
        line.appendChild(wrapper);
      });

      const tlIn = gsap.timeline({ delay: 0.15 });
      tlIn
        .to(eyebrow, { opacity: 1, y: 0, duration: 0.7, ease: 'power3.out' })
        .to('.pawpro-hero__headline span > span', {
          y: '0%',
          duration: 1.05,
          ease: 'expo.out',
          stagger: 0.08,
        }, '-=0.3')
        .to(sub,  { opacity: 1, y: 0, duration: 0.8, ease: 'power3.out' }, '-=0.5')
        .to(ctas, { opacity: 1, y: 0, duration: 0.7, ease: 'power3.out' }, '-=0.4')
        .to(kpis, { opacity: 1, y: 0, duration: 0.7, ease: 'power3.out' }, '-=0.4')
        .to(scrollIndicator, { opacity: 1, duration: 0.6, ease: 'power2.out' }, '-=0.2');

      // Note : l'effet "fade-out scroll-driven" du contenu hero a été supprimé
      // suite au feedback Fadi : le texte devenait invisible dès le 1er scroll,
      // empêchant la lecture du sub & des CTAs. On garde uniquement la load
      // timeline (entrée animée au chargement de la page).
    },

    // ── COUNT-UP — animated numbers in KPIs (one-shot) ───────────────────
    _initCountUps: function () {
      document.querySelectorAll('[data-countup]').forEach(el => {
        const target = parseFloat(el.dataset.countup);
        const decimals = el.dataset.countupDecimals ? parseInt(el.dataset.countupDecimals) : 0;
        const counter = { val: 0 };
        const formatter = function (n) {
          return decimals > 0
            ? n.toFixed(decimals).replace('.', ',')
            : Math.round(n).toLocaleString('fr-FR');
        };
        el.textContent = formatter(0);
        gsap.to(counter, {
          val: target,
          duration: 1.6,
          ease: 'power2.out',
          scrollTrigger: {
            trigger: el,
            start: 'top 90%',
            toggleActions: 'play none none none',
          },
          onUpdate: function () { el.textContent = formatter(counter.val); },
        });
      });
    },

    // ── MAGNETIC HOVERS — all primary CTAs (large size + hero) ───────────
    _initMagneticHovers: function () {
      const selector = '.pawpro-hero .pawpro-btn--primary, .pawpro-btn--primary.pawpro-btn--lg';
      document.querySelectorAll(selector).forEach(btn => {
        let bbox = null;
        let raf = null;
        const onEnter = () => { bbox = btn.getBoundingClientRect(); };
        const onMove = (e) => {
          if (!bbox) return;
          const x = e.clientX - (bbox.left + bbox.width / 2);
          const y = e.clientY - (bbox.top + bbox.height / 2);
          if (raf) cancelAnimationFrame(raf);
          raf = requestAnimationFrame(() => {
            gsap.to(btn, {
              x: x * 0.18,
              y: y * 0.22,
              duration: 0.5,
              ease: 'power3.out',
              overwrite: 'auto',
            });
          });
        };
        const onLeave = () => {
          if (raf) cancelAnimationFrame(raf);
          gsap.to(btn, {
            x: 0,
            y: 0,
            duration: 0.6,
            ease: 'elastic.out(1, 0.4)',
            overwrite: 'auto',
          });
        };
        btn.addEventListener('mouseenter', onEnter);
        btn.addEventListener('mousemove', onMove);
        btn.addEventListener('mouseleave', onLeave);
      });
    },

    // ── SUBTLE PARALLAX — selective images, GPU-friendly transforms ─────
    _initSubtleParallax: function () {
      const targets = [
        { sel: '.pawpro-story__visual-frame img', y: 8 },
      ];

      targets.forEach(({ sel, y }) => {
        document.querySelectorAll(sel).forEach(el => {
          gsap.to(el, {
            yPercent: y,
            ease: 'none',
            scrollTrigger: {
              trigger: el.closest('section') || el,
              start: 'top bottom',
              end: 'bottom top',
              scrub: 0.6,
            },
          });
        });
      });
    },

    // ── Reduced-motion fallback ──────────────────────────────────────────
    _initSimpleReveals: function () {
      document.querySelectorAll('[data-pawpro-reveal]').forEach(el => el.classList.add('is-revealed'));
      const hero = document.querySelector('[data-pawpro-hero-v2]');
      if (hero) {
        hero.querySelectorAll('[data-pawpro-text-reveal], [data-pawpro-hero-eyebrow], [data-pawpro-hero-sub], [data-pawpro-hero-ctas], [data-pawpro-hero-kpis], [data-pawpro-hero-scroll-cue]').forEach(el => {
          el.style.opacity = 1;
          el.style.transform = 'none';
        });
      }
    },
  };

  window.PAWPRO = PAWPRO;

  function init() { waitForLibs(function () { PAWPRO.motion.init(); }); }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
